/**
 * 單據上的「下次注意事項」提醒列(REQ-20260918002)。
 *
 * 唯讀、可收合、**絕不擋送出** —— 沒有 checkbox、不 disable 任何按鈕、
 * 送出時也不跳確認視窗。理由見 REQ-20260918002 §「為什麼不擋」:
 *  - 常駐提醒 + 勾選框解鎖送出鈕 已經被使用者否決過(REQ-20260903005 → 009,
 *    原話「目前這種防呆提示方式感覺表單很累贅」)。
 *  - 但也不能改成 REQ-20260903009 那種「送出才跳 ConfirmDialog」:
 *    這裡提醒的是**填單前要知道的規則**(顏色 / 厚度 / 價格),
 *    填完才跳出來就太晚了 —— 該改的欄位早就填完了。
 * 折衷就是這條非阻擋式的資訊列:看得到、不礙事。
 *
 * 沒有未完成項目(或還沒選到客戶 / 廠商)時 `return null`,畫面完全不變。
 *
 * REQ-20260922001:每一筆改成分類呈現(要點:商品 — 內容),
 * 但**行為一個字都沒動** —— 仍然唯讀、仍然可收合、仍然不擋送出。
 */
import { useState } from 'react';
import { useOpenReminders, type ReminderOwnerType } from './api';

interface Props {
  ownerType: ReminderOwnerType;
  /** 未選到客戶 / 廠商時傳 undefined —— 不會打 API */
  ownerId: number | undefined;
}

export function OpenRemindersBanner({ ownerType, ownerId }: Props): JSX.Element | null {
  const { data } = useOpenReminders(ownerType, ownerId);
  const [open, setOpen] = useState(true);

  const rows = data ?? [];
  if (rows.length === 0) return null;

  const label = ownerType === 'customer' ? '這家客戶' : '這家廠商';

  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-100 px-3 py-2 text-amber-800"
      data-testid="open-reminders-banner"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left text-sm font-medium hover:underline"
        data-testid="open-reminders-toggle"
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>
          🔔 {label}有 {rows.length} 件下次要注意的事
        </span>
      </button>
      {open ? (
        <ul className="mt-1 ml-6 list-disc text-sm" data-testid="open-reminders-list">
          {rows.map((r) => (
            <li key={r.id} className="whitespace-pre-wrap break-words">
              {/* 分類呈現但比分頁精簡 —— 這是資訊列不是清單頁,一筆就一行:
                  「要點:商品 — 內容」,沒填的段落連同分隔符一起省略。
                  日期不印:填單當下要的是「該改什麼」,不是那句話是哪天記的。 */}
              {r.keyPoint ? <span className="font-semibold">{r.keyPoint}：</span> : null}
              {r.productName ? <span>{r.productName} — </span> : null}
              {r.content}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
